import {
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  HostListener,
  input,
  OnDestroy,
  output,
  signal,
  ViewChild,
} from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';

export interface RoleOption {
  value: string;
  label: string;
}

@Component({
  selector: 'app-role-picker',
  templateUrl: './role-picker.component.html',
  styleUrl: './role-picker.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TranslateModule],
})
export class RolePickerComponent implements OnDestroy {
  value = input<string>('');
  options = input<RoleOption[]>([]);
  disabled = input<boolean>(false);

  valueChange = output<string>();

  isOpen = signal(false);

  // Position of the dropdown (fixed, relative to viewport)
  dropdownTop = signal(0);
  dropdownLeft = signal(0);
  dropdownWidth = signal(120);

  @ViewChild('triggerBtn') triggerBtn!: ElementRef<HTMLButtonElement>;

  selectedLabel = computed(() => {
    const found = this.options().find((o) => o.value === this.value());
    return found?.label ?? this.value();
  });

  toggle(): void {
    if (this.disabled()) return;

    if (!this.isOpen()) {
      this.recalcPosition();
    }
    this.isOpen.update((v) => !v);
  }

  private recalcPosition(): void {
    const rect = this.triggerBtn?.nativeElement?.getBoundingClientRect();
    if (!rect) return;
    this.dropdownTop.set(rect.bottom + 4);
    this.dropdownLeft.set(rect.left);
    this.dropdownWidth.set(rect.width);
  }

  select(value: string): void {
    if (value !== this.value()) {
      this.valueChange.emit(value);
    }
    this.isOpen.set(false);
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const host = this.el.nativeElement as HTMLElement;
    if (!host.contains(event.target as Node)) {
      this.isOpen.set(false);
    }
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.isOpen.set(false);
  }

  constructor(private el: ElementRef) {}

  ngOnDestroy(): void {
    this.isOpen.set(false);
  }
}
